import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { BeeLogo } from "../components/BeeLogo";
import { Heart, Moon, Sparkles, ShieldCheck, Wind, Leaf } from "lucide-react";
import t from "../lib/i18n";
import { Layout } from "../components/Layout";

const Pillar = ({ icon: Icon, title, body, testid }) => (
  <div className="bn-card p-7" data-testid={testid}>
    <div
      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
      style={{ background: "var(--bn-surface)" }}
      aria-hidden="true"
    >
      <Icon size={22} strokeWidth={2.2} style={{ color: "var(--bn-accent-terracotta)" }} />
    </div>
    <h3 className="display text-xl font-semibold mb-2">{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "var(--bn-text-secondary)" }}>{body}</p>
  </div>
);

export default function Landing() {
  const { language, parent } = useApp();

  return (
    <Layout hideNav={false}>
      <section className="grid md:grid-cols-2 gap-10 md:gap-16 items-center pt-6 md:pt-12">
        <div className="bn-fade">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: "var(--bn-surface)" }}>
            <BeeLogo size={28} variant="portrait" />
            <span className="text-sm font-semibold tracking-wide">BeeNest by IM•BEE</span>
          </div>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6" data-testid="hero-title">
            {t(language, "landTagSafeBefore")}
            <span style={{ color: "var(--bn-accent-terracotta)" }}>{t(language, "landTagSafe")}</span>
            {t(language, "landTagSafeAfter")}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed mb-8 max-w-xl" style={{ color: "var(--bn-text-secondary)" }}>
            {t(language, "landSubhead")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={parent ? "/dashboard" : "/auth"}
              className="bn-pill bn-primary-bg text-base"
              data-testid="cta-get-started"
            >
              {t(language, "getStarted")}
            </Link>
            <Link
              to="/auth?mode=login"
              className="bn-pill text-base"
              style={{ background: "var(--bn-surface-alt)", border: "1px solid var(--bn-border)" }}
              data-testid="cta-sign-in"
            >
              {t(language, "signIn")}
            </Link>
          </div>
          <div className="flex items-center gap-3 mt-8 text-sm" style={{ color: "var(--bn-text-secondary)" }}>
            <ShieldCheck size={16} aria-hidden="true" />
            <span>{t(language, "landTrustChip")}</span>
          </div>
        </div>

        <div className="relative bn-fade" style={{ animationDelay: "0.15s" }}>
          <div className="bn-card overflow-hidden" style={{ borderRadius: "2.5rem" }}>
            <img
              src="https://images.unsplash.com/photo-1533499966477-9333968a4e28?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt={t(language, "landHeroAlt")}
              className="w-full h-[420px] object-cover"
              data-testid="hero-image"
            />
          </div>
          <div className="absolute -bottom-4 -left-4 bn-card p-5 hidden md:flex items-center gap-3 bn-float">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--bn-primary)" }} aria-hidden="true">
              <Wind size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold">{t(language, "landBreatheSoftly")}</div>
              <div className="text-xs" style={{ color: "var(--bn-text-secondary)" }}>{t(language, "landBreathePace")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-6 md:gap-8 mt-20 md:mt-32">
        <Pillar testid="pillar-emotional" icon={Heart} title={t(language, "landPillarEmotionalTitle")} body={t(language, "landPillarEmotionalBody")} />
        <Pillar testid="pillar-bedtime" icon={Moon} title={t(language, "landPillarBedtimeTitle")} body={t(language, "landPillarBedtimeBody")} />
        <Pillar testid="pillar-garden" icon={Sparkles} title={t(language, "landPillarGardenTitle")} body={t(language, "landPillarGardenBody")} />
        <Pillar testid="pillar-sensory" icon={Leaf} title={t(language, "landPillarSensoryTitle")} body={t(language, "landPillarSensoryBody")} />
        <Pillar testid="pillar-trust" icon={ShieldCheck} title={t(language, "landPillarTrustTitle")} body={t(language, "landPillarTrustBody")} />
        <Pillar testid="pillar-slow" icon={Wind} title={t(language, "landPillarSlowTitle")} body={t(language, "landPillarSlowBody")} />
      </section>

      <section className="mt-24 md:mt-36 bn-card p-10 md:p-16 text-center bn-fade">
        <h2 className="display text-3xl md:text-4xl font-semibold mb-4">
          {t(language, "landBottomTitle")}
        </h2>
        <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: "var(--bn-text-secondary)" }}>
          {t(language, "landBottomBody")}
        </p>
        <Link to="/auth" className="bn-pill bn-primary-bg inline-block" data-testid="cta-bottom">
          {t(language, "getStarted")}
        </Link>
      </section>
    </Layout>
  );
}
